import { useContext } from "react";
import { useParams } from "react-router";

import {
  Box,
  Grid,
  Button,
  ButtonBase,
  CssBaseline,
  Typography,
  Divider,
  Snackbar,
  IconButton,
  useMediaQuery,
  Alert,
  useTheme
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import HelpIcon from "@mui/icons-material/Help";
import SettingsIcon from "@mui/icons-material/Settings";
import LogoutIcon from "@mui/icons-material/Logout";
import SettingsBackupRestoreIcon from "@mui/icons-material/SettingsBackupRestore";

import { hexToRgba } from "@aclymatepackages/converters";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHome, faLeaf } from "@fortawesome/free-solid-svg-icons";
import {
  faListCheck,
  faBadgeCheck,
  faBooks,
  faPodiumStar,
  faShirt
} from "@fortawesome/pro-solid-svg-icons";

import CommunicationCenter from "../modules/CommunicationCenter";
import ScoutBar from "../modules/ScoutBar";
import AddEmissionsMenu from "../modules/AddEmissionsMenu";
import AdminSelectCompanyMenu from "../modules/AdminSelectCompanyMenu";
import Link from "../atoms/mui/Link";

import { PlatformLayoutContextProvider } from "../../helpers/contexts/platformLayout";
import usePlatformLayoutContext from "../../helpers/contexts/platformLayout";
import { EmissionsContextProvider } from "../../helpers/contexts/emissions";
import AdminDataContextProvider, {
  AdminDataContext
} from "../../helpers/contexts/adminData";
import { useShowOnboardingPage } from "../../helpers/hooks/companyData";
import { useLogout } from "../../helpers/components/accountAccess";
import {
  useCachedFirebaseCrud,
  firebaseFieldDeleteObj
} from "../../helpers/firebase";
import { getAccountId } from "../../helpers/accountHelpers";
import { fetchOurApi } from "../../helpers/utils/apiCalls";

const AlertSnackbar = ({
  open,
  setOpen,
  onClose,
  alert,
  message,
  action,
  customButton,
  anchorOrigin = { vertical: "top", horizontal: "center" },
  autoHideDuration = 10000,
  ...otherProps
}) => {
  const handleClose = (_, reason) => {
    if (reason === "clickaway") {
      return;
    }

    return setOpen(false);
  };

  const ActionButtons = () => (
    <>
      {customButton
        ? customButton
        : !!onClose && (
            <Button
              onClick={() => {
                handleClose();
                onClose();
              }}
              size="small"
              id="snackbarActionBtn"
            >
              {action || "Yes"}
            </Button>
          )}
      <IconButton onClick={handleClose} size="small" id="snackbarCloseIconBtn">
        <CloseIcon />
      </IconButton>
    </>
  );

  return (
    <>
      {!!alert ? (
        <Snackbar
          open={open}
          onClose={handleClose}
          anchorOrigin={anchorOrigin}
          autoHideDuration={autoHideDuration}
          style={{ zIndex: 1501 }}
          {...otherProps}
        >
          <Alert
            severity={alert}
            onClose={handleClose}
            action={<ActionButtons />}
          >
            {message}
          </Alert>
        </Snackbar>
      ) : (
        <Snackbar
          open={open}
          message={message}
          onClose={handleClose}
          anchorOrigin={anchorOrigin}
          autoHideDuration={autoHideDuration}
          style={{ zIndex: 1501 }}
          {...otherProps}
          action={<ActionButtons />}
        />
      )}
    </>
  );
};

const NavLink = ({ name, icon, path }) => {
  const { palette } = useTheme();
  const { page } = useParams();
  const isActive = page === path;
  const color = isActive ? palette.primary.main : palette.text.secondary;

  return (
    <Grid item style={{ width: "100%" }}>
      <Link href={`/platform/company/${path}`} style={{ display: "block" }}>
        <ButtonBase
          id={`${name.replace(" ", "")}Btn`}
          style={{ width: "100%" }}
        >
          <Box
            px={2}
            py={0.75}
            style={{
              width: "100%",
              borderRadius: "10px",
              background: isActive
                ? hexToRgba(palette.primary.main, 0.12)
                : "transparent"
            }}
          >
            <Grid container spacing={1} flexWrap="nowrap" alignItems="center">
              <Grid item>
                <Box
                  style={{ width: "24px" }}
                  display="flex"
                  justifyContent="center"
                >
                  <FontAwesomeIcon
                    icon={icon}
                    style={{ color, fontSize: "20px" }}
                  />
                </Box>
              </Grid>
              <Grid item>
                <Typography
                  variant="h5"
                  noWrap
                  color={isActive ? "primary" : "textSecondary"}
                >
                  {name}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </ButtonBase>
      </Link>
    </Grid>
  );
};

const BottomNavItem = ({ name, icon, onClick, href }) => {
  const content = (
    <ButtonBase
      onClick={onClick}
      style={{ width: "100%" }}
      id={`${name.replace(" ", "")}Btn`}
    >
      <Box px={2} py={0.75} style={{ width: "100%", borderRadius: "10px" }}>
        <Grid container spacing={1} flexWrap="nowrap" alignItems="center">
          <Grid item>
            <Box
              style={{ width: "24px" }}
              display="flex"
              justifyContent="center"
            >
              {icon}
            </Box>
          </Grid>
          <Grid item>
            <Typography variant="h5" noWrap color="textSecondary">
              {name}
            </Typography>
          </Grid>
        </Grid>
      </Box>
    </ButtonBase>
  );

  if (href) {
    return <Link href={href}>{content}</Link>;
  }

  return content;
};

const NavListBox = ({ navItems }) => (
  <Box px={1} py={0.5}>
    <Grid container direction="column" spacing={0.5} style={{ width: "100%" }}>
      {navItems.map((link, idx) => (
        <NavLink {...link} key={`nav-link-${idx}`} />
      ))}
    </Grid>
  </Box>
);

const SideNav = () => {
  const [showOnboardingPage, showOnboardingPageLoading] =
    useShowOnboardingPage();

  const { communicationCenterTab, openCommunicationCenter, rootPath } =
    usePlatformLayoutContext();
  const { isUserAclymateAdmin } = useContext(AdminDataContext);
  const logout = useLogout();
  const { updateAccountData } = useCachedFirebaseCrud();

  const resetDemo = async () => {
    const accountId = getAccountId();
    fetchOurApi({
      path: "/transactions/reset-account",
      method: "POST",
      data: { accountId }
    });
    return updateAccountData({
      certification: {},
      accounting: firebaseFieldDeleteObj()
    });
  };

  const onboardingNavRow = showOnboardingPage
    ? [{ name: "Onboarding", icon: faListCheck, path: "onboarding" }]
    : [];

  const navPages = showOnboardingPageLoading
    ? []
    : [
        ...onboardingNavRow,
        {
          name: "Dashboard",
          icon: faHome,
          path: "dashboard"
        },
        { name: "Accounting", icon: faBooks, path: "accounting" },
        {
          name: "Offsets",
          icon: faLeaf,
          path: "purchase"
        },
        { name: "Certifications", icon: faBadgeCheck, path: "badges" }
      ];

  const addOns = showOnboardingPageLoading
    ? []
    : [
        { name: "Events", icon: faPodiumStar, path: "events" },
        { name: "Products", icon: faShirt, path: "products" }
      ];

  return (
    <>
      {communicationCenterTab && (
        <CommunicationCenter
          initialTab={communicationCenterTab}
          isSlideOpen={!!communicationCenterTab}
          setIsSlideOpen={() => openCommunicationCenter(null)}
        />
      )}
      <Box
        display="flex"
        flexDirection="column"
        style={{
          height: "100vh",
          background: "white",
          transition: "1s"
        }}
      >
        <Box
          p={1}
          style={{
            boxSizing: "content-box",
            height: "56px"
          }}
          display="flex"
          alignItems="center"
        >
          <img
            src="/images/aclymate-full-logo.png"
            alt="RWT logo"
            style={{ maxWidth: "260px" }}
          />
        </Box>
        <Divider />
        <Box
          flexGrow={1}
          display="flex"
          flexDirection="column"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            overflowY: "auto",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" }
          }}
        >
          <Box style={{ width: "100%" }}>
            <Box p={2} display="flex" justifyContent="center">
              <AddEmissionsMenu fullWidth />
            </Box>
            <NavListBox navItems={navPages} />
            <Divider />
            <NavListBox navItems={addOns} />
          </Box>
          <Box style={{ width: "100%" }}>
            <Box px={1} py={0.5}>
              <Grid container direction="column" spacing={0.5}>
                {process.env.REACT_APP_ENVIRONMENT !== "production" && (
                  <Grid item style={{ width: "100%" }}>
                    <span>
                      <BottomNavItem
                        name="Reset Account"
                        icon={
                          <SettingsBackupRestoreIcon
                            sx={{ color: "text.secondary", fontSize: 20 }}
                          />
                        }
                        onClick={resetDemo}
                      />
                    </span>
                  </Grid>
                )}
                {isUserAclymateAdmin && (
                  <Grid item style={{ width: "100%" }}>
                    <AdminSelectCompanyMenu />
                  </Grid>
                )}
                <Grid item style={{ width: "100%" }}>
                  <BottomNavItem
                    name="Settings"
                    icon={
                      <SettingsIcon
                        sx={{ color: "text.secondary", fontSize: 20 }}
                      />
                    }
                    href={`${rootPath}/settings/company`}
                  />
                </Grid>
                <Grid item style={{ width: "100%" }}>
                  <BottomNavItem
                    name="Logout"
                    icon={
                      <LogoutIcon
                        sx={{ color: "text.secondary", fontSize: 20 }}
                      />
                    }
                    onClick={logout}
                  />
                </Grid>
              </Grid>
            </Box>
            <Box px={1} py={1} justifyContent="center" display="flex">
              <Button
                variant="contained"
                startIcon={<HelpIcon />}
                color="primary"
                onClick={() => openCommunicationCenter("help")}
              >
                HELP
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

const Layout = ({ children }) => {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("md"));

  const { snackbarOpen, setSnackbarOpen, snackbarProps } =
    usePlatformLayoutContext();

  if (isSmall && process.env.REACT_APP_ENVIRONMENT === "production") {
    return (
      <Box
        p={2}
        style={{ height: `calc(100vh - ${theme.spacing(4)})` }}
        display="flex"
        alignItems="center"
        justifyContent="center"
        flexDirection="column"
      >
        <img
          src="/images/aclymate-vertical-logo.png"
          style={{ width: "80%", maxWidth: "350px" }}
          alt="aclymate logo"
        />
        <Box py={4}>
          <Typography variant="subtitle2" align="center" color="textPrimary">
            Aclymate needs to be accessed through a laptop or larger device size
            and will not work on a tablet or phone
          </Typography>
        </Box>
        <Button variant="contained" color="primary" href="https://aclymate.com">
          Go back to Website
        </Button>
      </Box>
    );
  }

  return (
    <>
      <AlertSnackbar
        open={snackbarOpen}
        setOpen={setSnackbarOpen}
        {...snackbarProps}
      />
      <CssBaseline />
      <Box
        display="flex"
        style={{ height: "100vh", overflow: "hidden" }}
        alignItems="stretch"
      >
        <SideNav />
        <Box
          flexGrow={1}
          style={{ overflow: "hidden", height: "100%" }}
          display="flex"
          flexDirection="column"
        >
          <ScoutBar />
          <Box
            flexGrow={1}
            style={{
              overflowY: "auto",
              backgroundColor: hexToRgba(theme.palette.primary.light, 0.08)
            }}
            position="relative"
          >
            {children}
          </Box>
        </Box>
      </Box>
    </>
  );
};

const CompanyLayout = ({ children, ...props }) => {
  return (
    <PlatformLayoutContextProvider {...props}>
      <AdminDataContextProvider>
        <EmissionsContextProvider>
          <Layout {...props}>{children}</Layout>
        </EmissionsContextProvider>
      </AdminDataContextProvider>
    </PlatformLayoutContextProvider>
  );
};
export default CompanyLayout;
